import { useContext, useEffect, useState, forwardRef, useImperativeHandle } from "react";
import ReactLoading from "react-loading";
import { v4 as uuidv4 } from "uuid";
import { OutorgaContext } from "../../../../../../contexts/Outorga";
import { getEquipamentosByMonitorado, getCamposEquipamento } from "../../../../../../services/equipamentos";
import helper from "../../../../../../utils/helper";
import { buildCustomParamsPayload, mapSavedCustomParamsToState } from "../../../../../../utils/outorga/parametros/grantCustomParamsMapper";
import CustomParamRow from "./CustomParamRow";

const CustomParams = forwardRef((props, ref) => {
    const { getGrantDetails, getCustomParameters, setUnsavedCustomParams, deleteGrantParameters, isLoadingGrantCustomParams, setNotAddedCustomParams } = useContext(OutorgaContext);

    const [equipmentList, setEquipmentList] = useState([]);
    const [paramsList, setParamsList] = useState([]);
    const [newParam, setNewParam] = useState(getEmptyParam());
    const [isInitializing, setIsInitializing] = useState(true);
    const [lastSyncedCustomParams, setLastSyncedCustomParams] = useState(null);
    const [errors, setErrors] = useState({});

    function getEmptyParam() {
        return {
            id: null,
            name: "",
            isActive: true,
            equipment: null,
            equipmentField: null,
            alertValueStart: "",
            alertValueEnd: "",
            criticalValueStart: "",
            criticalValueEnd: "",
            exceededValueStart: "",
            exceededValueEnd: "",
            scale: null,
            groupBy: null
        };
    }

    useImperativeHandle(ref, () => ({
        handleSave,
        validateParameters
    }));

    useEffect(() => {
        loadEquipments();
    }, []);

    useEffect(() => {
        syncSavedParams();
    }, [getCustomParameters()]);

    useEffect(() => {
        const hasValue = Object.entries(newParam).some(
            ([key, value]) => key !== "isActive" && value
        );
        if(hasValue) {
            setNotAddedCustomParams(true)
            return
        }
        setNotAddedCustomParams()
    }, [newParam]);

    const loadEquipments = async () => {
        const monitorado = getGrantDetails()?.basicInfo?.monitorado;

        if (!monitorado) {
            setIsInitializing(false);
            return;
        }

        try {
            const resp = await getEquipamentosByMonitorado(monitorado);

            setEquipmentList(resp?.equipamentos || []);

        } catch (err) {
            console.error(err);
        }

        setIsInitializing(false);
    };

    const syncSavedParams = async () => {
        const savedParams = getCustomParameters();

        if (!savedParams || savedParams === lastSyncedCustomParams) return;

        const fieldsCache = {};
        const loadFieldsFn = async (equipId) => {
            if (!equipId) return;

            if (!fieldsCache[equipId]) {
                try {
                    const resp = await getCamposEquipamento(equipId);

                    fieldsCache[equipId] = resp || [];

                } catch (err) {
                    console.error(err);

                    fieldsCache[equipId] = [];
                }
            }
        };

        loadFieldsFn.getCache = (equipId) => fieldsCache[equipId];

        const { paramsList } = await mapSavedCustomParamsToState({ savedParams, loadFieldsFn });

        setParamsList(paramsList);
        setLastSyncedCustomParams(savedParams);
    };

    const syncPayload = (list) => {
        const payload = buildCustomParamsPayload(list);

        setUnsavedCustomParams(payload.condicoes);
    };

    const updateParam = (updated) => {
        const newList = paramsList.map((param) => param.id === updated.id ? updated : param);

        setParamsList(newList);
        syncPayload(newList);
    };

    const handleAdd = () => {
        if (
            !newParam.name ||
            !newParam.equipment ||
            !newParam.equipmentField ||
            !newParam.alertValueStart ||
            !newParam.alertValueEnd ||
            !newParam.criticalValueStart ||
            !newParam.criticalValueEnd ||
            !newParam.exceededValueStart ||
            !newParam.exceededValueEnd ||
            !newParam.scale ||
            !newParam.groupBy
        ) {
            helper.dispatchEvent("showAviso", { message: "Preencha os campos antes de adicionar." });

            return;
        }

        const newItem = {
            ...newParam,
            id: uuidv4(),
            isNewRegister: true
        };

        const newList = [...paramsList, newItem];
        setParamsList(newList);
        setNewParam(getEmptyParam());
        syncPayload(newList);
    };

    const handleRemove = (param) => {
        if (!param.isNewRegister) {
            deleteGrantParameters(props.grantId, param.id);

            return;
        }

        const newList = paramsList.filter(p => p.id !== param.id);
        setParamsList(newList);
        syncPayload(newList);
    };

    const validateParameters = () => {
        const errorsObj = {};

        for (let param of paramsList) {
            if (!param.isActive) continue;

            errorsObj[param.id] = {
                name: !param.name,
                equipment: !param.equipment,
                equipmentField: !param.equipmentField,
                alertValueStart: param.alertValueStart === "",
                alertValueEnd: param.alertValueEnd === "",
                criticalValueStart: param.criticalValueStart === "",
                criticalValueEnd: param.criticalValueEnd === "",
                exceededValueStart: param.exceededValueStart === "",
                exceededValueEnd: param.exceededValueEnd === "",
                scale: !param.scale,
                groupBy: !param.groupBy
            };
        }

        setErrors(errorsObj);

        return {
            customIsValid: !Object.values(errorsObj).some(paramErrors => Object.values(paramErrors).some(Boolean)),
        }
    };

    const handleSave = () => {
        if (!paramsList?.length) return;
        const payload = buildCustomParamsPayload(paramsList, true);
        return payload;
    };

    return (
        <div className="grant-custom-params">
            {(isInitializing || isLoadingGrantCustomParams) && (
                <div className="grant-custom-params__loading">
                    <ReactLoading
                        type="spin"
                        color="var(--background-menu)"
                        width={40}
                    />
                </div>
            )}

            <div className="grant-custom-params__body">
                <CustomParamRow
                    isNew
                    param={newParam}
                    equipmentList={equipmentList}
                    onChange={setNewParam}
                    onAdd={handleAdd}
                />
            </div>

            {paramsList.length > 0 && <hr />}

            <div className="grant-custom-params__list">
                {paramsList.map((param) => (
                    <CustomParamRow
                        key={param.id}
                        param={param}
                        equipmentList={equipmentList}
                        onChange={updateParam}
                        onDelete={handleRemove}
                        errors={errors[param.id] || {}}
                    />
                ))}
            </div>
        </div>
    );
});

export default CustomParams;